import './PublicProfileView.css';

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatNumber = (value) => (value == null ? '—' : Number(value).toLocaleString());

const formatRate = (value) => (
  value == null ? '—' : Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })
);

const formatDate = (iso) => {
  if (!iso) return '—';
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'
  });
};

const Tile = ({ label, value, note }) => (
  <div className="profile-tile">
    <div className="profile-tile-value">{value}</div>
    <div className="profile-tile-label">{label}</div>
    {note && <div className="profile-tile-note">{note}</div>}
  </div>
);

const BarChart = ({ data, labelFor, ariaLabel }) => {
  const max = Math.max(1, ...data.map((d) => d.value));
  const width = 100;
  const barWidth = width / data.length;
  return (
    <svg
      className="profile-chart"
      viewBox={`0 0 ${width} 40`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel}
    >
      {data.map((d, i) => {
        const height = d.value === 0 ? 0.6 : Math.max(1.5, (d.value / max) * 38);
        return (
          <rect
            key={d.key}
            x={i * barWidth + barWidth * 0.12}
            y={40 - height}
            width={barWidth * 0.76}
            height={height}
            rx={0.4}
            className={d.value === 0 ? 'profile-bar profile-bar--empty' : 'profile-bar'}
          >
            <title>{labelFor(d)}</title>
          </rect>
        );
      })}
    </svg>
  );
};

/**
 * The public profile body. Presentational: the page owns fetching, so this
 * renders from a profile object alone.
 */
const PublicProfileView = ({ profile, scope, onScopeChange }) => {
  const { reader, totals, rates, streaks, highlights, series, weekdayChapters, badges = [], level, rank } = profile;
  const seasonScope = scope === 'season';
  const wordsKnown = totals.words != null;
  const testamentTotal = totals.oldTestamentReads + totals.newTestamentReads;
  const oldPct = testamentTotal ? Math.round((totals.oldTestamentReads / testamentTotal) * 100) : 0;

  return (
    <div className="public-profile">
      <header className="public-profile-header">
        <h1>{reader.name}</h1>
        <p className="public-profile-sub">
          {level?.title && <span>{level.title}</span>}
          {rank != null && <span>Rank #{rank} this season</span>}
          {streaks.current > 0 && <span>🔥 {streaks.current}-day streak</span>}
        </p>
        <div className="public-profile-scope" role="group" aria-label="Time range">
          <button type="button" aria-pressed={seasonScope} onClick={() => onScopeChange('season')}>This season</button>
          <button type="button" aria-pressed={!seasonScope} onClick={() => onScopeChange('all')}>All time</button>
        </div>
      </header>

      <section aria-label="Totals">
        <div className="profile-tiles">
          <Tile label="Chapters" value={formatNumber(totals.chapters)} />
          <Tile label="Verses" value={formatNumber(totals.verses)} />
          <Tile label="Words" value={formatNumber(totals.words)} note={wordsKnown ? null : 'Coming soon'} />
          <Tile label="Days reporting" value={formatNumber(rates.daysReporting)} note={`of ${formatNumber(rates.daysElapsed)} days`} />
        </div>
      </section>

      <section aria-label="Daily pace">
        <h2>Daily pace</h2>
        <div className="profile-tiles">
          <Tile label="Chapters / day" value={formatRate(rates.chaptersPerDay)} note={`${formatRate(rates.chaptersPerReadingDay)} on reading days`} />
          <Tile label="Verses / day" value={formatRate(rates.versesPerDay)} note={`${formatRate(rates.versesPerReadingDay)} on reading days`} />
          <Tile label="Words / day" value={formatRate(rates.wordsPerDay)} note={wordsKnown ? `${formatRate(rates.wordsPerReadingDay)} on reading days` : 'Coming soon'} />
        </div>
      </section>

      {series.length > 0 && (
      <section aria-label="Daily chapters">
        <h2>Since the challenge began</h2>
        <BarChart
          data={series.map((d) => ({ key: d.date, value: d.chapters, date: d.date }))}
          labelFor={(d) => `${formatDate(d.date)}: ${d.value} chapters`}
          ariaLabel="Chapters read per day since the challenge began"
        />
      </section>
      )}

      <section aria-label="Streaks and highlights">
        <h2>Highlights</h2>
        <div className="profile-tiles">
          <Tile label="Current streak" value={`${formatNumber(streaks.current)}d`} />
          <Tile label="Longest streak" value={`${formatNumber(streaks.longest)}d`} />
          <Tile
            label="Best day"
            value={highlights.bestDay ? `${highlights.bestDay.chapters} ch` : '—'}
            note={highlights.bestDay ? formatDate(highlights.bestDay.date) : null}
          />
          <Tile
            label="Best 7 days"
            value={highlights.bestWeek ? `${highlights.bestWeek.chapters} ch` : '—'}
            note={highlights.bestWeek ? `from ${formatDate(highlights.bestWeek.start)}` : null}
          />
          <Tile label="Favorite day" value={highlights.busiestWeekday || '—'} />
          <Tile
            label="Most-read book"
            value={highlights.mostReadBook?.book || '—'}
            note={highlights.mostReadBook ? `${highlights.mostReadBook.chapters} chapters` : null}
          />
          <Tile label="Books finished" value={`${totals.booksFinished}/66`} />
          <Tile label="Re-reads" value={formatNumber(totals.rereads)} />
        </div>
      </section>

      <section aria-label="Bible progress">
        <h2>Bible progress</h2>
        <p className="profile-percent"><strong>{totals.percentOfBible}%</strong> of the Bible ({formatNumber(totals.uniqueChapters)}/1,189 chapters)</p>
        <div
          className="profile-bar-track"
          role="progressbar"
          aria-valuenow={Math.round(totals.percentOfBible)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Bible read"
        >
          <span style={{ width: `${Math.min(100, totals.percentOfBible)}%` }} />
        </div>
        {testamentTotal > 0 && (
          <p className="profile-note">
            {oldPct}% Old Testament · {100 - oldPct}% New Testament
            {highlights.favoriteTestament && ` · leans ${highlights.favoriteTestament} Testament`}
          </p>
        )}
        {seasonScope && highlights.projectedFinish && (
          <p className="profile-note">At this pace: a full read-through by {formatDate(highlights.projectedFinish)}</p>
        )}
      </section>

      <section aria-label="Reading by weekday">
        <h2>By weekday</h2>
        <BarChart
          data={weekdayChapters.map((value, i) => ({ key: WEEKDAY_SHORT[i], value }))}
          labelFor={(d) => `${d.key}: ${d.value} chapters`}
          ariaLabel="Chapters read by day of the week"
        />
        <div className="profile-weekday-labels" aria-hidden="true">
          {WEEKDAY_SHORT.map((day) => <span key={day}>{day}</span>)}
        </div>
      </section>

      {badges.length > 0 && (
        <section aria-label="Badges">
          <h2>Badges</h2>
          <ul className="profile-badges">
            {badges.map((badge) => (
              <li key={badge.id} title={badge.description || undefined}>{badge.name}</li>
            ))}
          </ul>
        </section>
      )}

      <p className="profile-footnote">
        First read {formatDate(highlights.firstReadDate)} · last read {formatDate(highlights.lastReadDate)}
      </p>
    </div>
  );
};

export default PublicProfileView;
